// Video Section JavaScript
(() => {
  'use strict';

  // Utility functions
  const debounce = (func, wait) => {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  };

  const getBreakpoint = () => {
    if (window.innerWidth >= 768) return 'desktop';
    return 'mobile';
  };

  const updateVideoSources = (video, sources) => {
    if (!video || !sources || !sources.length) return;

    // Remove existing sources
    const existingSources = video.querySelectorAll('source');
    existingSources.forEach(source => source.remove());

    // Add new sources
    sources.forEach(source => {
      const sourceElement = document.createElement('source');
      sourceElement.src = source.url;
      sourceElement.type = source.mime_type;
      video.appendChild(sourceElement);
    });

    // Reload video to apply new sources
    video.load();
  };

  const getVideoConfig = (sectionId) => {
    const script = document.getElementById(`hero-video-${sectionId}--sources`);
    if (!script) return null;

    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      console.warn('Failed to parse video config:', error);
      return null;
    }
  };

  // Video Section Class
  class VideoSection {
    constructor(element) {
      this.element = element;
      this.video = element.querySelector('[data-video-element]');
      this.playButton = element.querySelector('[data-play-button]');
      this.isPlaying = false;
      this.hasAutoPlayed = false;

      if (!this.video && !this.playButton) return;

      this.sectionId = this.#extractSectionId();
      this.config = getVideoConfig(this.sectionId);

      this.#init();
    }

    #extractSectionId() {
      const classes = this.element.className.split(' ');
      const sectionClass = classes.find(cls => cls.startsWith('video-section-'));
      return sectionClass ? sectionClass.replace('video-section-', '') : null;
    }

    #init() {
      this.#setupEventListeners();
      this.#setupResponsiveVideo();
      this.#setupAutoplay();
    }

    #setupEventListeners() {
      // Play button click
      if (this.playButton) {
        this.playButton.addEventListener('click', this.#handlePlayButtonClick.bind(this));
      }

      // Video click when playing (to pause)
      if (this.video) {
        this.video.addEventListener('click', this.#handleVideoClick.bind(this));
        this.video.addEventListener('ended', this.#handleVideoEnded.bind(this));
      }
    }

    #setupResponsiveVideo() {
      if (!this.video || !this.config) return;

      let currentBreakpoint = getBreakpoint();
      this.#updateVideoForBreakpoint(currentBreakpoint);

      // Handle resize
      const handleResize = () => {
        const newBreakpoint = getBreakpoint();
        if (newBreakpoint === currentBreakpoint) return;

        currentBreakpoint = newBreakpoint;
        this.#updateVideoForBreakpoint(currentBreakpoint);
      };

      window.addEventListener('resize', debounce(handleResize, 150));
    }

    #updateVideoForBreakpoint(breakpoint) {
      if (!this.config) return;

      let sources;
      if (breakpoint === 'desktop') {
        sources = this.config.video_desktop;
      } else {
        sources = this.config.video_mobile;
      }

      if (sources && sources.length > 0) {
        updateVideoSources(this.video, sources);
      }
    }

    #handlePlayButtonClick(event) {
      event.preventDefault();

      if (!this.video) return;

      this.#playVideo();
    }

    #handleVideoClick() {
      if (this.isPlaying) {
        this.#pauseVideo();
      }
    }

    #handleVideoEnded() {
      this.#resetVideo();
    }

    #playVideo() {
      if (!this.video) return;

      this.video.play().then(() => {
        this.isPlaying = true;
        this.video.controls = true;
        this.element.classList.add('video-section--playing');
      }).catch((error) => {
        console.warn('Video playback failed:', error);
      });
    }

    #pauseVideo() {
      if (!this.video) return;

      this.video.pause();
      this.isPlaying = false;
      this.element.classList.remove('video-section--playing');
    }

    #resetVideo() {
      if (!this.video) return;

      this.video.currentTime = 0;
      this.isPlaying = false;
      this.element.classList.remove('video-section--playing');
    }

    #setupAutoplay() {
      if (!this.config || !this.config.autoplay || !this.video) return;

      // Add autoplay class to element
      this.element.classList.add('video-section--autoplay');

      // Create intersection observer
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && !this.hasAutoPlayed) {
              this.#handleAutoplay();
            }
          });
        },
        {
          threshold: 0.5, // Trigger when 50% of the video is visible
          rootMargin: '0px'
        }
      );

      observer.observe(this.element);
    }

    #handleAutoplay() {
      if (!this.video || this.hasAutoPlayed) return;

      this.hasAutoPlayed = true;

      // Set video attributes for autoplay
      this.video.muted = true; // Required for autoplay in most browsers
      this.video.loop = true;

      this.#playVideo();
    }
  }

  // Initialize video sections
  const initVideoSections = () => {
    const videoSections = document.querySelectorAll('.video-section');
    videoSections.forEach(section => new VideoSection(section));
  };

  // Initialize on DOM content loaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initVideoSections);
  } else {
    initVideoSections();
  }

  // Re-initialize on Shopify section load (theme editor)
  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.querySelector('.video-section');
    if (section) {
      new VideoSection(section);
    }
  });
})();
